import React, { useContext, useEffect, useState, useRef } from 'react';
import { Row, Col, Stack } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import 'assets/styles/common.scss';
import { LocaleContext } from 'routes/Router';
import { FormattedMessage, useIntl } from 'react-intl';
import MuiBreadcrumbs from 'components/common/MuiBreadcrumbs';
import {
  providerAdminAppointments,
  providerAdminAddAppointments,
} from 'routes/AppUrls';
import LabelWithInput from 'components/common/Input/LabelWithInput';
import SelectInput from 'components/common/Input/SelectInput';
import PhoneNumberInput from 'components/common/Input/PhoneNumberInput';
import ReusableButton from 'components/common/Button';
import { useDispatch, useSelector } from 'react-redux';
import ToastNotification from 'components/common/Toast';
import CheckValidation from 'utils/Validation';
import { addAppointmentsCallAPI } from 'store/ProviderAdmin/addAppointments/action';
import { getAppointmentDaysProviderCallAPI } from 'store/AppointmentDaysList/action';
import { getCustomerProviderCallAPI } from 'store/ProviderAdmin/getCustomerList/action';
import { getUsersListCallAPI } from 'store/ProviderAdmin/getUsersList/action';
import { registerUserCallAPI } from 'store/ProviderAdmin/registerUser/action';
import { getServiceOfferedCallAPI } from 'store/ProviderAdmin/serviceOffered/action';
import { getSpecialistProviderCallAPI } from 'store/ProviderAdmin/getSpecialistProvider/action';
import { getTimeSlotsCallAPI } from 'store/ProviderAdmin/getTimeSlots/action';
import { webCheckoutCallAPI } from 'store/ProviderAdmin/webCheckout/action';
import { getServiceStylistsCallAPI } from 'store/ProviderAdmin/getServiceStylists/action';
import CircularProgress from '@mui/material/CircularProgress';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { getUserRole } from 'utils/general';
import CommonModal from 'components/common/Modal';
import crossIcon from 'assets/images/crossIcon.svg';
import moment from 'moment';
import { Box } from '@mui/material';

const AddAppointment = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [conf, setConf] = ToastNotification();
  const intl = useIntl();
  const { locale } = useContext(LocaleContext);
  const userRole = getUserRole();
  const profileData = useSelector((state) => state?.userMeReducer?.data?.data);

  const phoneNumberInputRef = useRef(null);
  const customerSearchInputRef = useRef(null);
  const customerSearchContainerRef = useRef(null);
  const serviceSearchContainerRef = useRef(null);
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [showCustomerResults, setShowCustomerResults] = useState(false);
  const [isAddCustomerModalOpen, setIsAddCustomerModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [serviceSearchTerm, setServiceSearchTerm] = useState('');
  const [serviceSearchResults, setServiceSearchResults] = useState([]);
  const [showServiceResults, setShowServiceResults] = useState(false);

  const addAppointmentLoader = useSelector(
    (state) => state?.addAppointmentsReducer?.loading
  );
  const webCheckoutLoader = useSelector(
    (state) => state?.webCheckoutReducer?.loading
  );
  const customerListLoading = useSelector(
    (state) => state?.getCustomerReducer?.loading
  );
  const usersListData = useSelector(
    (state) => state?.getUsersListReducer?.data
  );
  const usersListLoading = useSelector(
    (state) => state?.getUsersListReducer?.loading
  );
  const servicesList = useSelector(
    (state) => state?.getProviderServiceReducer?.data?.data
  );
  const servicesLoading = useSelector(
    (state) => state?.getProviderServiceReducer?.loading
  );
  const [flattenedServicesList, setFlattenedServicesList] = useState([]);
  const specialistList = useSelector(
    (state) => state?.getSpecialistProviderReducer?.data?.data
  );
  const specialistLoading = useSelector(
    (state) => state?.getSpecialistProviderReducer?.loading
  );

  // New Customer Form State
  const [newCustomerForm, setNewCustomerForm] = useState({
    firstName: '',
    lastName: '',
    gender: '',
    phoneNumber: '',
    countryCode: '+974',
  });
  const [newCustomerErrors, setNewCustomerErrors] = useState({});
  const [isNewCustomerPhoneValid, setIsNewCustomerPhoneValid] = useState(true);

  // Selected Services State
  const [selectedServices, setSelectedServices] = useState([]);
  const [serviceStylists, setServiceStylists] = useState({});

  // Appointment Form State
  const [formValue, setFormValue] = useState({
    appointmentDate: '',
    appointmentTime: '',
    notes: '',
    paymentType: '', // 'now' or 'later'
  });

  const [isError, setIsError] = useState(null);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const appointmentDaysList = useSelector(
    (state) => state?.getAppointmentDaysReducer?.data?.data
  );

  const [breadcrumbsList] = useState([
    {
      title: <FormattedMessage id="Appointments" />,
      Link: providerAdminAppointments(locale),
      isDisbale: false,
    },
    {
      title: <FormattedMessage id="Book Appointment" />,
      Link: '',
      isDisbale: true,
    },
  ]);

  useEffect(() => {
    fetchServicesList();
    if (userRole === 'salon-admin') {
      fetchSpecialistList();
    }
  }, []);

  useEffect(() => {
    if (searchTerm.length >= 2 && showCustomerResults) {
      const debounceTimer = setTimeout(() => {
        searchCustomers();
      }, 300);
      return () => clearTimeout(debounceTimer);
    } else if (searchTerm.length === 0) {
      // Don't clear results when search term is empty - let loadAllUsers handle it
      // Only clear if we're not in focus state
    } else {
      // Clear results only for very short search terms (1 character)
      if (!showCustomerResults) {
        setCustomerSearchResults([]);
      }
    }
  }, [searchTerm, showCustomerResults]);

  useEffect(() => {
    if (usersListData) {
      // Handle different response structures
      let usersList = [];
      if (Array.isArray(usersListData)) {
        usersList = usersListData;
      } else if (usersListData?.data && Array.isArray(usersListData.data)) {
        usersList = usersListData.data;
      } else if (
        usersListData?.results &&
        Array.isArray(usersListData.results)
      ) {
        usersList = usersListData.results;
      }

      if (usersList.length > 0) {
        setCustomerSearchResults(usersList);
        // Don't auto-show dropdown on data load - only show when user clicks on input
      } else if (
        usersListData &&
        !Array.isArray(usersListData) &&
        !usersListData?.data &&
        !usersListData?.results
      ) {
        // If data exists but not in expected format, try to use it directly
        setCustomerSearchResults([]);
      } else {
        setCustomerSearchResults([]);
      }
    } else {
      // Reset when data is cleared
      if (searchTerm.length === 0) {
        setCustomerSearchResults([]);
      }
    }
  }, [usersListData, searchTerm]);

  useEffect(() => {
    // Fetch days list on mount
    if (!appointmentDaysList || appointmentDaysList.length === 0) {
      dispatch(
        getAppointmentDaysProviderCallAPI(
          {},
          {
            run: false,
            navigate: navigate,
          }
        )
      );
    }
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        customerSearchContainerRef.current &&
        !customerSearchContainerRef.current.contains(event.target)
      ) {
        setShowCustomerResults(false);
      }
      if (
        serviceSearchContainerRef.current &&
        !serviceSearchContainerRef.current.contains(event.target)
      ) {
        setShowServiceResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchServicesList = () => {
    dispatch(
      getServiceOfferedCallAPI(
        {},
        {
          run: true,
          handleFunction: (res) => {
            // Services are directly in data array
            const allServices = [];
            const data = res?.data?.data || res?.data || [];
            data.forEach((service) => {
              allServices.push({
                id: service.id,
                name: service.name,
                service_name: service.name,
                sub_service_name: service.name,
                structure: service.structure,
                slug: service.slug,
                price: service.price || 0,
                currency: service.currency || 'USD',
                duration: service.time_required_in_mints || 0,
                time_required_in_mints: service.time_required_in_mints || 0,
              });
            });
            setFlattenedServicesList(allServices);
            setServiceSearchResults(allServices);
          },
          navigate: navigate,
        }
      )
    );
  };

  const loadAllServices = () => {
    if (flattenedServicesList.length > 0) {
      setServiceSearchResults(flattenedServicesList);
    } else {
      fetchServicesList();
    }
  };

  const searchServices = () => {
    if (serviceSearchTerm.length >= 2) {
      const filtered = flattenedServicesList.filter((service) =>
        (service.name || service.service_name || service.sub_service_name || '')
          .toLowerCase()
          .includes(serviceSearchTerm.toLowerCase())
      );
      setServiceSearchResults(filtered);
    } else {
      setServiceSearchResults(flattenedServicesList);
    }
  };

  const fetchSpecialistList = () => {
    const providerId = profileData?.provider_details?.id;
    dispatch(
      getSpecialistProviderCallAPI(
        {},
        {
          id: providerId,
          type: 'onboarded',
          run: true,
          handleFunction: (res) => {
            // Specialist list is stored in Redux
          },
          navigate: navigate,
        }
      )
    );
  };

  const loadAllUsers = () => {
    setShowCustomerResults(true); // Show dropdown immediately
    dispatch(
      getUsersListCallAPI(
        {},
        {
          run: true,
          handleFunction: (res) => {
            // Users list will be set via useEffect from Redux state
            // Also handle response directly here as fallback
            if (res?.data) {
              let usersList = [];
              if (Array.isArray(res.data)) {
                usersList = res.data;
              } else if (res.data?.data && Array.isArray(res.data.data)) {
                usersList = res.data.data;
              } else if (res.data?.results && Array.isArray(res.data.results)) {
                usersList = res.data.results;
              }
              if (usersList.length > 0) {
                setCustomerSearchResults(usersList);
                setShowCustomerResults(true);
              }
            }
          },
          navigate: navigate,
        }
      )
    );
  };

  const searchCustomers = () => {
    setShowCustomerResults(true); // Show dropdown immediately
    dispatch(
      getUsersListCallAPI(
        { search: searchTerm },
        {
          run: true,
          handleFunction: (res) => {
            // Users list will be set via useEffect from Redux state
            // Also handle response directly here as fallback
            if (res?.data) {
              let usersList = [];
              if (Array.isArray(res.data)) {
                usersList = res.data;
              } else if (res.data?.data && Array.isArray(res.data.data)) {
                usersList = res.data.data;
              } else if (res.data?.results && Array.isArray(res.data.results)) {
                usersList = res.data.results;
              }
              if (usersList.length > 0) {
                setCustomerSearchResults(usersList);
                setShowCustomerResults(true);
              }
            }
          },
          navigate: navigate,
        }
      )
    );
  };

  const handleCustomerSelect = (customer) => {
    const customerName = customer?.full_name || 'Unknown';
    setSelectedCustomer(customer);
    setSearchTerm(customerName);
    setShowCustomerResults(false);
  };

  const handleClearCustomer = () => {
    setSelectedCustomer(null);
    setSearchTerm('');
    setCustomerSearchResults([]);
    setShowCustomerResults(false);
  };

  const handleAddCustomerClick = () => {
    setIsAddCustomerModalOpen(true);
  };

  const handleNewCustomerChange = (e) => {
    const { name, value } = e.target;
    setNewCustomerForm({ ...newCustomerForm, [name]: value });
    if (newCustomerErrors[name]) {
      setNewCustomerErrors({ ...newCustomerErrors, [name]: '' });
    }
  };

  const handleNewCustomerPhoneChange = (e) => {
    const phoneNumberValue = e.target.value.replace(/\D/g, '');
    setNewCustomerForm({
      ...newCustomerForm,
      phoneNumber: phoneNumberValue,
    });

    const fullPhoneNumber = `+${newCustomerForm?.countryCode}${phoneNumberValue}`;
    const parsedPhoneNumber = parsePhoneNumberFromString(fullPhoneNumber);
    if (parsedPhoneNumber) {
      setIsNewCustomerPhoneValid(parsedPhoneNumber.isValid());
    } else {
      setIsNewCustomerPhoneValid(false);
    }
  };

  const handleNewCustomerCountryCodeChange = (value) => {
    setNewCustomerForm({ ...newCustomerForm, countryCode: value });
  };

  const handleSaveNewCustomer = () => {
    const errors = {};
    if (!newCustomerForm.firstName.trim()) {
      errors.firstName = 'First Name is required';
    }
    if (!newCustomerForm.gender) {
      errors.gender = 'Gender is required';
    }
    if (!newCustomerForm.phoneNumber) {
      errors.phoneNumber = 'Phone Number is required';
    }
    if (!isNewCustomerPhoneValid) {
      errors.phoneNumber = 'Invalid phone number';
    }

    if (Object.keys(errors).length > 0) {
      setNewCustomerErrors(errors);
      return;
    }

    const fullName =
      `${newCustomerForm.firstName} ${newCustomerForm.lastName || ''}`.trim();
    const apiData = {
      phone: newCustomerForm.phoneNumber,
      full_name: fullName,
      gender: newCustomerForm.gender,
      country_code: newCustomerForm.countryCode,
      user_role: 'walk-in-user',
      last_name: newCustomerForm.lastName || '',
    };

    dispatch(
      registerUserCallAPI(apiData, {
        run: true,
        handleFunction: (res) => {
          if (res?.data) {
            const responseData = res.data?.data || res.data;
            const newCustomer = {
              id: responseData.id || responseData.user_id,
              full_name: fullName,
              first_name: newCustomerForm.firstName,
              last_name: newCustomerForm.lastName,
              phone: newCustomerForm.phoneNumber,
              phone_number: newCustomerForm.phoneNumber,
              country_code: newCustomerForm.countryCode,
              gender: newCustomerForm.gender,
              email: '',
            };

            setSelectedCustomer(newCustomer);
            setIsAddCustomerModalOpen(false);
            setNewCustomerForm({
              firstName: '',
              lastName: '',
              gender: '',
              phoneNumber: '',
              countryCode: '+974',
            });
            setNewCustomerErrors({});
            setIsNewCustomerPhoneValid(false);

            setConf({
              msg: res.data?.message || 'Customer created successfully',
              variant: 'success',
            });
          }
        },
        toast: true,
        handleToastError: (error) => {
          if (error?.response?.data) {
            setConf({
              msg: error.response.data.message || 'Failed to create customer',
              variant: 'error',
            });
          } else {
            setConf({
              msg: 'Failed to create customer',
              variant: 'error',
            });
          }
        },
        navigate: navigate,
      })
    );
  };

  const handleServiceSelect = (service) => {
    const isSelected = selectedServices.some((s) => s.id === service.id);
    if (isSelected) {
      setSelectedServices(selectedServices.filter((s) => s.id !== service.id));
      setAvailableTimeSlots([]);
      setFormValue((prev) => ({
        ...prev,
        appointmentTime: '',
        appointmentDate: '',
      }));
      // Remove stylists for removed service
      const updatedStylists = { ...serviceStylists };
      delete updatedStylists[service.id];
      setServiceStylists(updatedStylists);
    } else {
      setSelectedServices([
        ...selectedServices,
        {
          ...service,
          selectedStylist: null,
        },
      ]);
      // Fetch stylists for the selected service
      fetchServiceStylists(service.id);
    }
  };

  const fetchServiceStylists = (serviceId) => {
    dispatch(
      getServiceStylistsCallAPI(
        {},
        {
          serviceId: serviceId,
          run: true,
          handleFunction: (res) => {
            const stylists = res?.data?.data || [];
            setServiceStylists((prev) => ({
              ...prev,
              [serviceId]: stylists,
            }));
          },
          navigate: navigate,
        }
      )
    );
  };

  const handleStylistSelect = (serviceId, stylistId) => {
    setSelectedServices(
      selectedServices.map((service) =>
        service.id === serviceId
          ? { ...service, selectedStylist: stylistId }
          : service
      )
    );
  };

  const removeService = (serviceId) => {
    setSelectedServices(selectedServices.filter((s) => s.id !== serviceId));
    const updatedStylists = { ...serviceStylists };
    delete updatedStylists[serviceId];
    setServiceStylists(updatedStylists);
  };

  const calculateTotalPrice = () => {
    return selectedServices.reduce((total, service) => {
      return total + (parseFloat(service?.price) || 0);
    }, 0);
  };

  const calculateTotalDuration = () => {
    return selectedServices.reduce((total, service) => {
      return (
        total +
        (parseInt(service?.time_required_in_mints || service?.duration) || 0)
      );
    }, 0);
  };

  const handleDateChange = (e) => {
    const selectedDate = e.target.value;
    setFormValue({ ...formValue, appointmentDate: selectedDate });
    setFormValue((prev) => ({ ...prev, appointmentTime: '' }));
    setAvailableTimeSlots([]);

    if (selectedDate && selectedServices.length > 0) {
      // Get day of week from date
      const dateObj = new Date(selectedDate);
      const dayNames = [
        'sunday',
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
      ];
      const dayName = dayNames[dateObj.getDay()];

      // Find day ID from days list
      const daysList = appointmentDaysList || [];
      const dayData = daysList.find((day) => day.slug === dayName);

      if (dayData && profileData?.provider_details?.id) {
        const providerId = profileData.provider_details.id;
        const servicesIds = selectedServices.map((s) => s.id);

        // Fetch time slots
        dispatch(
          getTimeSlotsCallAPI(
            {
              provider_id: providerId,
              services_ids: servicesIds,
              for_date: selectedDate,
              day_of_week: dayData.id,
            },
            {
              run: true,
              handleFunction: (res) => {
                const slotsData = res?.data?.data?.slots || [];
                // Filter slots where status is true
                const availableSlots = slotsData
                  .filter((slot) => slot.status === true)
                  .map((slot) => {
                    // Format time from "HH:MM:SS" to "HH:MM"
                    const startTime = slot.start.substring(0, 5);
                    return startTime;
                  });
                setAvailableTimeSlots(availableSlots);
              },
              navigate: navigate,
            }
          )
        );
      }
    }
  };

  const handleTimeChange = (time) => {
    setFormValue({ ...formValue, appointmentTime: time });
  };

  const handlePaymentTypeSelect = (type) => {
    setFormValue({ ...formValue, paymentType: type });
    if (type === 'now') {
      // TODO: Open payment modal
      setConf({
        msg: 'Payment modal will open here',
        variant: 'info',
      });
    }
  };

  const handleSubmit = () => {
    if (!selectedCustomer) {
      return setConf({
        msg: 'Please select a customer',
        variant: 'error',
      });
    }

    if (selectedServices.length === 0) {
      return setConf({
        msg: 'Please select at least one service',
        variant: 'error',
      });
    }

    if (!formValue.appointmentDate) {
      return setConf({
        msg: 'Please select appointment date',
        variant: 'error',
      });
    }

    if (!formValue.appointmentTime) {
      return setConf({
        msg: 'Please select appointment time',
        variant: 'error',
      });
    }

    // Payment is optional - removed validation
    // if (!formValue.paymentType) {
    //   return setConf({
    //     msg: 'Please select payment option',
    //     variant: 'error',
    //   });
    // }

    // Validate stylist assignment for salon-admin
    if (userRole === 'salon-admin') {
      const servicesWithoutStylist = selectedServices.filter(
        (s) => !s.selectedStylist
      );
      if (servicesWithoutStylist.length > 0) {
        return setConf({
          msg: 'Please assign a stylist to all services',
          variant: 'error',
        });
      }
    }

    // Calculate end time
    const startTime = moment(
      `${formValue.appointmentDate} ${formValue.appointmentTime}`,
      'YYYY-MM-DD HH:mm'
    );
    const endTime = startTime.clone().add(calculateTotalDuration(), 'minutes');

    // Format dates for API
    const slotStartTime = startTime.format('YYYY-MM-DDTHH:mm:ss');
    const slotEndTime = endTime.format('YYYY-MM-DDTHH:mm:ss');

    const body = {
      user_id: selectedCustomer.id,
      services: selectedServices.map((service) => ({
        service_id: service.id,
        stylist_id: service.selectedStylist || null,
      })),
      service_location_type: 'walk-in',
      slot_date: formValue.appointmentDate,
      slot_start_time: slotStartTime,
      slot_end_time: slotEndTime,
      special_requirement: formValue.notes || '',
    };

    dispatch(
      webCheckoutCallAPI(body, {
        navigate: navigate,
        run: true,
        handleFunction: (res) => handleRedirect(res),
        toast: true,
        handleToastError: (res) => handleRedirectError(res),
      })
    );
  };

  const handleRedirect = (res) => {
    setConf({
      msg: res?.data?.message || 'Appointment booked successfully',
      variant: 'success',
    });
    navigate(providerAdminAppointments(locale));
  };

  const handleRedirectError = (res) => {
    setConf({
      msg: res?.data?.message || 'Failed to book appointment',
      variant: 'error',
    });
  };

  return (
    <Row className="bc-F2F3F6 m-0 p-0">
      <Col
        className={
          locale === 'en'
            ? 'commonEnContainer lg:ml-left'
            : 'commonArContainer lg:mr-right'
        }
      >
        <MuiBreadcrumbs size="14" list={breadcrumbsList} />
        <Row className="bc-FFFFFF m-0 p-16 mt-3">
          <h2 className="fc-292D32 fs-32 fw-400 ff-Akkurat_Bold m-0">
            <FormattedMessage id="Book Appointment" />
          </h2>

          <Row className="m-0 p-0 mt-4">
            {/* Customer Selection */}
            <Col xs={12} className="mt-3">
              <p className="p-0 m-0 fs-16 fc-292D32 fw-400 ff-Akkurat_Bold mb-0">
                <FormattedMessage id="Search Customer" />
                <span style={{ color: 'red' }}> *</span>
              </p>
              <div
                className="relative"
                ref={customerSearchContainerRef}
                style={{ marginTop: '0px' }}
              >
                <LabelWithInput
                  placeholder={intl.formatMessage({
                    id: 'Search by name or phone number',
                  })}
                  name="customerSearch"
                  type="text"
                  value={searchTerm}
                  marginLabelTop={0}
                  marginTop={0}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    // Don't show dropdown on change, only on focus
                  }}
                  onFocus={() => {
                    // Always load users when clicking inside input
                    if (searchTerm.length === 0) {
                      loadAllUsers();
                      setShowCustomerResults(true);
                    } else if (customerSearchResults.length > 0) {
                      setShowCustomerResults(true);
                    } else if (searchTerm.length >= 2) {
                      setShowCustomerResults(true);
                      searchCustomers();
                    } else {
                      // Show dropdown even if search term is short
                      setShowCustomerResults(true);
                      if (customerSearchResults.length === 0) {
                        loadAllUsers();
                      }
                    }
                  }}
                />
                {searchTerm && (
                  <img
                    src={crossIcon}
                    alt="Clear"
                    className="cursor-pointer"
                    onClick={() => {
                      setSearchTerm('');
                      setCustomerSearchResults([]);
                      setShowCustomerResults(false);
                    }}
                    style={{
                      position: 'absolute',
                      right: '15px',
                      top: 'calc(25px + 25px)',
                      transform: 'translateY(-50%)',
                      height: '20px',
                      width: '20px',
                      zIndex: 10,
                    }}
                  />
                )}
                {showCustomerResults && customerSearchResults.length > 0 && (
                  <div
                    className="absolute z-10 bg-white border border-gray-200 rounded-md shadow-lg mt-1 w-full max-h-60 overflow-y-auto"
                    style={{ top: '100%' }}
                  >
                    {customerSearchResults.map((customer) => (
                      <div
                        key={customer.id || customer.user_id}
                        className="p-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100"
                        onClick={() => handleCustomerSelect(customer)}
                      >
                        <p className="p-0 m-0 fs-14 fc-292D32 fw-400 ff-Akkurat_Bold">
                          {customer.full_name || 'Unknown'}
                        </p>
                        <p className="p-0 m-0 fs-12 fc-9A9A9A fw-400 ff-Akkurat">
                          {customer.phone || ''}{' '}
                          {customer.email ? `| ${customer.email}` : ''}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                {showCustomerResults &&
                  customerSearchResults.length === 0 &&
                  searchTerm.length >= 2 && (
                    <div className="absolute z-10 bg-white border border-gray-200 rounded-md shadow-lg mt-1 w-full p-3">
                      <ReusableButton
                        fullWidth={false}
                        variant="contained"
                        fontSize="14px"
                        fontWeight="400"
                        padding="8px 16px"
                        bgColor="#C56616"
                        brColor="#C56616"
                        sx={{ color: '#FFFFFF' }}
                        fontFamily="Akkurat"
                        onClick={handleAddCustomerClick}
                      >
                        + <FormattedMessage id="Add Customer" />
                      </ReusableButton>
                    </div>
                  )}
              </div>
              {selectedCustomer && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md position-relative">
                  <button
                    type="button"
                    onClick={handleClearCustomer}
                    className="position-absolute"
                    style={{
                      top: '8px',
                      right: '8px',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    title="Clear selection"
                  >
                    <img
                      src={crossIcon}
                      alt="Clear"
                      style={{ height: '16px', width: '16px' }}
                    />
                  </button>
                  <p className="p-0 m-0 fs-14 fc-292D32 fw-400 ff-Akkurat_Bold pr-4">
                    Selected: {selectedCustomer.full_name || 'Unknown'}
                  </p>
                  <p className="p-0 m-0 fs-12 fc-9A9A9A fw-400 ff-Akkurat">
                    {selectedCustomer.phone || ''}{' '}
                    {selectedCustomer.email
                      ? `| ${selectedCustomer.email}`
                      : ''}
                  </p>
                </div>
              )}
            </Col>

            {/* Service Selection */}
            <Col xs={12} className="mt-4">
              <p className="p-0 m-0 fs-16 fc-292D32 fw-400 ff-Akkurat_Bold mb-0">
                <FormattedMessage id="Select Services" />
                <span style={{ color: 'red' }}> *</span>
              </p>
              <div className="relative" ref={serviceSearchContainerRef}>
                <LabelWithInput
                  placeholder={intl.formatMessage({ id: 'Search Services' })}
                  name="serviceSearch"
                  type="text"
                  value={serviceSearchTerm}
                  marginLabelTop={0}
                  marginTop={0}
                  onChange={(e) => {
                    setServiceSearchTerm(e.target.value);
                  }}
                  onFocus={() => {
                    if (serviceSearchTerm.length === 0) {
                      loadAllServices();
                      setShowServiceResults(true);
                    } else if (serviceSearchResults.length > 0) {
                      setShowServiceResults(true);
                    } else if (serviceSearchTerm.length >= 2) {
                      setShowServiceResults(true);
                      searchServices();
                    } else {
                      setShowServiceResults(true);
                      if (serviceSearchResults.length === 0) {
                        loadAllServices();
                      }
                    }
                  }}
                />
                {serviceSearchTerm && (
                  <img
                    src={crossIcon}
                    alt="Clear"
                    className="cursor-pointer"
                    onClick={() => {
                      setServiceSearchTerm('');
                      setServiceSearchResults(flattenedServicesList);
                      setShowServiceResults(false);
                    }}
                    style={{
                      position: 'absolute',
                      right: '15px',
                      top: 'calc(25px + 45px)',
                      transform: 'translateY(-50%)',
                      height: '20px',
                      width: '20px',
                      zIndex: 10,
                    }}
                  />
                )}
                {showServiceResults && serviceSearchResults.length > 0 && (
                  <div
                    className="absolute z-10 bg-white border border-gray-200 rounded-md shadow-lg mt-1 w-full max-h-60 overflow-y-auto"
                    style={{ top: '100%' }}
                  >
                    {serviceSearchResults.map((service) => (
                      <div
                        key={service.id}
                        className="p-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100"
                        onClick={() => {
                          handleServiceSelect(service);
                          setServiceSearchTerm('');
                          setShowServiceResults(false);
                        }}
                      >
                        <p className="p-0 m-0 fs-14 fc-292D32 fw-400 ff-Akkurat_Bold">
                          {service.name ||
                            service.service_name ||
                            service.sub_service_name ||
                            'Unknown'}
                        </p>
                        <p className="p-0 m-0 fs-12 fc-9A9A9A fw-400 ff-Akkurat">
                          Price: {service.price || '0'}{' '}
                          {service.currency || 'USD'} | Duration:{' '}
                          {service.time_required_in_mints ||
                            service.duration ||
                            '0'}{' '}
                          min
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Col>

            {selectedServices.length > 0 && (
              <Col xs={12} className="mt-4">
                <p className="p-0 m-0 fs-16 fc-292D32 fw-400 ff-Akkurat_Bold mb-3">
                  <FormattedMessage id="Selected Services" />
                </p>
                {selectedServices.map((service, index) => (
                  <div
                    key={service.id}
                    className="p-3 mb-3 border border-gray-200 rounded-md bg-white"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="p-0 m-0 fs-14 fc-292D32 fw-400 ff-Akkurat_Bold">
                          {service.name || service.service_name}
                        </p>
                        <p className="p-0 m-0 fs-12 fc-9A9A9A fw-400 ff-Akkurat mt-1">
                          Price: {service.price || '0'}{' '}
                          {service.currency || 'USD'} | Duration:{' '}
                          {service.time_required_in_mints ||
                            service.duration ||
                            '0'}{' '}
                          min
                        </p>
                        {userRole === 'salon-admin' &&
                          (() => {
                            const stylistOptions =
                              serviceStylists[service.id]?.map((stylist) => ({
                                id: stylist.id || stylist.stylist_id,
                                name:
                                  stylist.name ||
                                  stylist.stylist_name ||
                                  stylist.stylist_name_en,
                              })) || [];
                            const selectedStylistOption = stylistOptions.find(
                              (s) => s.id === service.selectedStylist
                            );
                            return (
                              <div style={{ marginTop: 0, paddingTop: 0 }}>
                                <SelectInput
                                  placeholder={intl.formatMessage({
                                    id: 'Select Stylist',
                                  })}
                                  name={`stylist_${service.id}`}
                                  data={stylistOptions}
                                  value={selectedStylistOption?.name || null}
                                  marginTop={0}
                                  getValue={(e, selectedItem) =>
                                    handleStylistSelect(
                                      service.id,
                                      selectedItem?.id
                                    )
                                  }
                                />
                              </div>
                            );
                          })()}
                      </div>
                      <img
                        src={crossIcon}
                        alt="remove"
                        className="cursor-pointer ml-2"
                        style={{ height: '20px', width: '20px' }}
                        onClick={() => removeService(service.id)}
                      />
                    </div>
                  </div>
                ))}
                <div className="mt-3 p-3 bg-gray-50 rounded-md mb-2">
                  <div className="flex justify-between items-center">
                    <p className="p-0 m-0 fs-14 fc-292D32 fw-400 ff-Akkurat_Bold">
                      <FormattedMessage id="Total Duration" />:
                    </p>
                    <p className="p-0 m-0 fs-14 fc-292D32 fw-400 ff-Akkurat_Bold">
                      {calculateTotalDuration()} min
                    </p>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <p className="p-0 m-0 fs-14 fc-292D32 fw-400 ff-Akkurat_Bold">
                      <FormattedMessage id="Total Price" />:
                    </p>
                    <p className="p-0 m-0 fs-16 fc-C56616 fw-600 ff-Akkurat_Bold">
                      {calculateTotalPrice()}{' '}
                      {selectedServices.length > 0
                        ? selectedServices[0]?.currency || 'USD'
                        : 'USD'}
                    </p>
                  </div>
                </div>
              </Col>
            )}

            {/* Date & Time Selection */}
            <Col xs={12} md={6} className="mt-3">
              <LabelWithInput
                label={<FormattedMessage id="Appointment Date" />}
                required={true}
                placeholder={intl.formatMessage({ id: 'Appointment Date' })}
                name="appointmentDate"
                type="date"
                value={formValue?.appointmentDate}
                onChange={handleDateChange}
                min={moment().startOf('day').format('YYYY-MM-DD')}
                errorText={
                  isError &&
                  isError?.appointmentDate?.error &&
                  isError?.appointmentDate?.errorMessage
                }
                marginLabelTop={0}
                marginTop={0}
              />
            </Col>

            <Col xs={12} md={6} className="mt-3">
              <SelectInput
                label={<FormattedMessage id="Select Time Slot" />}
                required
                data={availableTimeSlots.map((time) => ({
                  id: time,
                  name: time,
                }))}
                value={formValue.appointmentTime || ''}
                getValue={(e, selectedItem) => {
                  handleTimeChange(selectedItem?.id || selectedItem);
                }}
                placeholder={intl.formatMessage({ id: 'Select Time Slot' })}
                marginLabelTop={0}
              />
            </Col>

            {/* Service Type */}
            <Col xs={12} md={6} className="mt-3 hidden">
              <LabelWithInput
                label={<FormattedMessage id="Service Type" />}
                name="serviceType"
                type="text"
                value="Walk-In"
                marginLabelTop={0}
                marginTop={0}
              />
            </Col>

            {/* Notes */}
            <Col xs={12} className="mt-3">
              <LabelWithInput
                label={<FormattedMessage id="Notes" />}
                placeholder={intl.formatMessage({ id: 'Notes' })}
                height="100"
                multiline
                name="notes"
                value={formValue?.notes}
                onChange={(e) =>
                  setFormValue({ ...formValue, notes: e.target.value })
                }
              />
            </Col>

            {/* Action Buttons */}
            <Col xs={12} className="mt-4">
              <div className="flex justify-end gap-3">
                <ReusableButton
                  fullWidth={false}
                  variant="outlined"
                  fontSize="16px"
                  fontWeight="400"
                  padding="12px 25px"
                  height="44px"
                  bgColor="transparent"
                  brColor="#292D32"
                  fontFamily="Akkurat"
                  onClick={() => navigate(-1)}
                  sx={{ color: '#292D32' }}
                >
                  <FormattedMessage id="Cancel" />
                </ReusableButton>
                <ReusableButton
                  fullWidth={false}
                  variant={
                    formValue.paymentType === 'now' ? 'contained' : 'outlined'
                  }
                  fontSize="16px"
                  fontWeight="400"
                  padding="12px 25px"
                  height="44px"
                  bgColor={
                    formValue.paymentType === 'now' ? '#C56616' : 'transparent'
                  }
                  brColor="#C56616"
                  fontFamily="Akkurat"
                  onClick={() => handlePaymentTypeSelect('now')}
                  sx={{
                    color:
                      formValue.paymentType === 'now' ? '#FFFFFF' : '#292D32',
                  }}
                >
                  <FormattedMessage id="Take Payment Now" />
                </ReusableButton>
                <ReusableButton
                  fullWidth={false}
                  variant="contained"
                  fontSize="16px"
                  fontWeight="400"
                  padding="12px 25px"
                  height="44px"
                  bgColor="#292D32"
                  sx={{ color: '#F5F5F5' }}
                  fontFamily="Akkurat"
                  endIcon={
                    (addAppointmentLoader || webCheckoutLoader) && (
                      <CircularProgress
                        style={{
                          color: 'white',
                          width: '20px',
                          height: '20px',
                        }}
                      />
                    )
                  }
                  onClick={handleSubmit}
                >
                  <FormattedMessage id="Confirm Appointment" />
                </ReusableButton>
              </div>
            </Col>
          </Row>
        </Row>

        {/* Add Customer Modal */}
        <CommonModal
          modalActive={isAddCustomerModalOpen}
          handleClose={() => {
            setIsAddCustomerModalOpen(false);
            setNewCustomerForm({
              firstName: '',
              lastName: '',
              gender: '',
              phoneNumber: '',
              countryCode: '+974',
            });
            setNewCustomerErrors({});
          }}
          width={600}
          innerContent={
            <Box className="p-4">
              <h2 className="fc-292D32 fs-24 fw-400 ff-Akkurat_Bold m-0 mb-4">
                <FormattedMessage id="Add New Customer" />
              </h2>
              <Row className="m-0 p-0">
                <Col xs={12} md={6} className="mt-3">
                  <LabelWithInput
                    label={<FormattedMessage id="First Name" />}
                    required={true}
                    placeholder={intl.formatMessage({ id: 'First Name' })}
                    name="firstName"
                    type="text"
                    value={newCustomerForm?.firstName}
                    onChange={handleNewCustomerChange}
                    errorText={newCustomerErrors.firstName}
                  />
                </Col>
                <Col xs={12} md={6} className="mt-3">
                  <LabelWithInput
                    label={<FormattedMessage id="Last Name" />}
                    placeholder={intl.formatMessage({ id: 'Last Name' })}
                    name="lastName"
                    type="text"
                    value={newCustomerForm?.lastName}
                    onChange={handleNewCustomerChange}
                  />
                </Col>
                <Col xs={12} md={6} className="mt-3">
                  <SelectInput
                    label={<FormattedMessage id="Gender" />}
                    className="capitalize"
                    required={true}
                    placeholder={intl.formatMessage({ id: 'Select Gender' })}
                    name="gender"
                    value={newCustomerForm?.gender || ''}
                    data={[
                      { id: 'male', name: 'Male' },
                      { id: 'female', name: 'Female' },
                    ]}
                    SelectProps={{
                      renderValue: (selected) => {
                        if (!selected) return '';
                        if (typeof selected === 'object' && selected.name) {
                          return selected.name;
                        }
                        return '';
                      },
                    }}
                    getValue={(e, selectedItem) => {
                      setNewCustomerForm({
                        ...newCustomerForm,
                        gender: selectedItem?.id || selectedItem,
                      });
                    }}
                    errorText={newCustomerErrors.gender}
                  />
                </Col>
                <Col xs={12} md={6} className="mt-3 mb-2">
                  <PhoneNumberInput
                    label={<FormattedMessage id="Phone Number" />}
                    required={true}
                    country={'qa'}
                    value={newCustomerForm?.countryCode}
                    name={'phoneNumber'}
                    onCountryCodeChange={(e) =>
                      handleNewCustomerCountryCodeChange(e)
                    }
                    onPhoneNumberChange={handleNewCustomerPhoneChange}
                    phoneNumber={newCustomerForm.phoneNumber}
                    phoneNumberRef={phoneNumberInputRef}
                    isPhoneNumberValid={isNewCustomerPhoneValid}
                    isError={
                      newCustomerErrors.phoneNumber || !isNewCustomerPhoneValid
                        ? newCustomerErrors.phoneNumber ||
                          'Invalid phone number for selected country code'
                        : null
                    }
                    errorMessage={
                      newCustomerErrors.phoneNumber ||
                      'Invalid phone number for selected country code'
                    }
                    placeholder=""
                  />
                </Col>
                <div className="flex justify-end gap-3 mt-4 w-100">
                  <ReusableButton
                    fullWidth={false}
                    variant="outlined"
                    fontSize="16px"
                    fontWeight="400"
                    padding="12px 25px"
                    height="44px"
                    bgColor="transparent"
                    brColor="#292D32"
                    fontFamily="Akkurat"
                    onClick={() => {
                      setIsAddCustomerModalOpen(false);
                      setNewCustomerForm({
                        firstName: '',
                        lastName: '',
                        gender: '',
                        phoneNumber: '',
                        countryCode: '+974',
                      });
                      setNewCustomerErrors({});
                    }}
                    sx={{ color: '#292D32' }}
                  >
                    <FormattedMessage id="Cancel" />
                  </ReusableButton>
                  <ReusableButton
                    fullWidth={false}
                    variant="contained"
                    fontSize="16px"
                    fontWeight="400"
                    padding="12px 25px"
                    height="44px"
                    bgColor="#292D32"
                    fontFamily="Akkurat"
                    onClick={handleSaveNewCustomer}
                    sx={{ color: '#F5F5F5' }}
                  >
                    <FormattedMessage id="Save Customer" />
                  </ReusableButton>
                </div>
              </Row>
            </Box>
          }
        />
      </Col>
    </Row>
  );
};

export default AddAppointment;
